import type { TrackerAvailabilityState, TrackerAvailabilityStatus, TrackerContributor, TrackerAvailabilityRange } from './tracker';
import type { PendingRequest } from './pendingRequests';
import type { Request } from '../types';
import { splitUserSCNames } from './opportunities';

// ============================================================================
// Product → solution mapping
//
// Request products are the raw Workfront form values (e.g. "AEM: Sites").
// Contributor availability is keyed by the tracker's solution ids (e.g.
// aem-sites-eds-ms-on-prem). This curated dictionary bridges the two.
// ============================================================================

/** Lowercase, strip punctuation/whitespace so product strings can be looked up. */
export function normalizeProductKey(product: string): string {
  return product.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const PRODUCT_TO_SOLUTION: Record<string, string> = {
  dicja:'cja-b2c-b2b',
  dicjab2b:'cja-b2b',
  dianalytics:'adobe-analytics',
  dimixmodeler:'mix-modeler',
  // dicontentanalytics:'NO MATCH',

  paaep:'aep-overall',
  partcdp:'rtcdp-b2c',
  // pacollaboration:'NO MATCH',

  cjmajo:'ajo-b2c',
  cjmajob2b:'ajo-b2b',
  cjmcampaign:'adobe-campaign-v7-v8',
  // cjmexpaccelerator:'NO MATCH',
  cjmtarget:'adobe-target',

  marketoengage:'marketo-engage',
  marketomeasure:'marketo-measure',

  aiagentorchestrator:'aep-ai-agents-orchestrator',
  brandconcierge:'brand-concierge',

  aemsites:'aem-sites-eds-ms-on-prem',
  aemassets:'aem-assets-dynamic-media-and-content-hub',
  aemforms:'aem-forms',
  aemsitesoptimizer:'aem-sites-optimizer',
  aemllmoptimizer:'llm-optimizer',
  // aemlearningmanager:'NO MATCH',

  workfront:'workfront-wf-planning-wf-fusion',
  genstudio:'genstudio-for-performance-marketing',
  commerce:'adobe-commerce',

  // dicja: 'cja-b2c-b2b',
  // dicjab2b: 'cja-b2b',
  // dianalytics: 'adobe-analytics',
  // dimixmodeler: 'mix-modeler',
  // dicontentanalytics: 'adobe-analytics',

  // aemassets: 'aem-assets-dynamic-media-and-content-hub',
  // aemsites: 'aem-sites-eds-ms-on-prem',
  // aemllmoptimizer: 'llm-optimizer',
  // aemsitesoptimizer: 'aem-sites-optimizer',
  // aemforms: 'aem-forms',
  // aemmultisolution: 'aem-multi-solution',
  // aem: 'aem-multi-solution',

  // cja: 'cja-b2c-b2b',
  // customerjourneyanalytics: 'cja-b2c-b2b',
  // journeyoptimizer: 'ajo-b2c',
  // cjmaio: 'ajo-b2c',
  // ajo: 'ajo-b2c',
  
  // analytics: 'adobe-analytics',
  // marketoengage: 'marketo-engage',
  // marketo: 'marketo-engage',
  // marketomeasure: 'marketo-measure',
  // commerce: 'adobe-commerce',
  // adobecommerce: 'adobe-commerce',
  // adobetarget: 'adobe-target',
  // cjmtarget: 'adobe-target',
  // target: 'adobe-target',
  // rtcdp: 'rtcdp-b2c',
  // pandartcdp: 'rtcdp-b2c',
  // workfront: 'workfront-wf-planning-wf-fusion',
  // aep: 'aep-overall',
};

/** Map a raw request product string to a tracker solution id, or null if unmapped. */
export function productToSolutionId(product: string): string | null {
  return PRODUCT_TO_SOLUTION[normalizeProductKey(product)] ?? null;
}

// ============================================================================
// Availability
// ============================================================================

/** Parse "L4" → 4; unknown levels rank as 0. */
export function levelRank(level: string | null | undefined): number {
  if (!level) return 0;
  const n = Number(String(level).replace(/\D/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/** Shift a YYYY-MM-DD date by a signed number of days, returned as YYYY-MM-DD. */
export function addDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * Trim the tracker's availability state down to the AI-assign payload: only the
 * candidate contributors (the SCs surfaced by the current page's suggestions)
 * and, for date-availability overrides, only ranges overlapping the next
 * `horizonDays` (default 14, today included). Keeps the model's reasoning
 * context small and scoped to the pool it is allowed to pick from.
 */
export function trimAvailabilityState(
  state: TrackerAvailabilityState,
  candidateIds: string[],
  today: string,
  horizonDays = 14
): TrackerAvailabilityState {
  const candidates = new Set(candidateIds);
  const endOfWindow = addDays(today, Math.max(0, horizonDays - 1));
  const isCandidate = (id: string): boolean => candidates.has(id);

  const keepContributorMaps = <T>(map?: Record<string, Record<string, T>>) => {
    if (!map) return undefined;
    const out: Record<string, Record<string, T>> = {};
    for (const [id, inner] of Object.entries(map)) {
      if (isCandidate(id)) out[id] = inner;
    }
    return out;
  };

  const keepRangeMaps = () => {
    if (!state.dateAvailability) return undefined;
    const out: Record<string, Record<string, TrackerAvailabilityRange[]>> = {};
    for (const [id, solutions] of Object.entries(state.dateAvailability)) {
      if (!isCandidate(id)) continue;
      const trimmed: Record<string, TrackerAvailabilityRange[]> = {};
      for (const [solutionId, ranges] of Object.entries(solutions)) {
        const kept = ranges.filter(r => r.startDate <= endOfWindow && r.endDate >= today);
        if (kept.length > 0) trimmed[solutionId] = kept;
      }
      if (Object.keys(trimmed).length > 0) out[id] = trimmed;
    }
    return out;
  };

  return {
    ...state,
    contributors: (state.contributors ?? []).filter(c => isCandidate(c.id)),
    availability: keepContributorMaps(state.availability),
    experienceLevels: keepContributorMaps(state.experienceLevels),
    weeklyScheduleToday: keepContributorMaps(state.weeklyScheduleToday),
    dateAvailability: keepRangeMaps(),
  };
}

/** Out-of-office if today falls within the contributor's OOO window. */
export function isOutOfOfficeToday(contributor: TrackerContributor, today: string): boolean {
  if (!contributor.oooStartDate || !contributor.oooEndDate) return false;
  return contributor.oooStartDate <= today && today <= contributor.oooEndDate;
}

/**
 * Effective per-solution availability for a contributor on a given day, per the
 * agreed precedence (highest first): weeklyScheduleToday → dateAvailability
 * (active range covering today) → availability. A contributor out of office
 * today is 'unavailable' regardless of the layers.
 */
export function effectiveAvailability(
  state: TrackerAvailabilityState,
  contributorId: string,
  solutionId: string,
  today: string
): TrackerAvailabilityStatus | undefined {
  const contributor = state.contributors?.find(c => c.id === contributorId);
  if (contributor && isOutOfOfficeToday(contributor, today)) return 'unavailable';

  const weekly = state.weeklyScheduleToday?.[contributorId]?.[solutionId];
  if (weekly) return weekly;

  const ranges = state.dateAvailability?.[contributorId]?.[solutionId];
  if (ranges) {
    const active = ranges.find(r => r.startDate <= today && today <= r.endDate);
    if (active) return active.status;
  }

  return state.availability?.[contributorId]?.[solutionId];
}

// ============================================================================
// Suggestions
// ============================================================================

export interface SuggestionProductMatch {
  product: string;
  solutionId: string;
  level: string | null;
  status: TrackerAvailabilityStatus | undefined;
}

export interface RequestSuggestion {
  contributorId: string;
  name: string;
  email?: string;
  region?: string;
  /** One entry per distinct mapped solution of the request, with that solution's level/status. */
  matches: SuggestionProductMatch[];
  /** True when available for every distinct mapped solution. */
  full: boolean;
  /** Number of distinct mapped solutions the SC is available for. Primary ordering key. */
  matchBreadth: number;
  /** Highest experience level among the matched (available) solutions only. */
  maxLevel: number;
  /** Number of in-progress (INP) requests currently assigned to this SC. */
  inProgressCount: number;
}

export interface RequestSuggestions {
  full: RequestSuggestion[];
  partial: RequestSuggestion[];
}

/**
 * SC suggestions and AI assign only apply to requests that are still waiting to
 * be routed: Workfront status NEW and no Workfront assignee.
 */
export function isSuggestionEligible(
  request: Pick<PendingRequest, 'status' | 'workfrontAssignee' | 'workfrontAssignees'>
): boolean {
  if (String(request.status ?? '').trim().toUpperCase() !== 'NEW') return false;
  if (request.workfrontAssignee) return false;
  return (request.workfrontAssignees ?? []).length === 0;
}

function sortSuggestions(list: RequestSuggestion[]): RequestSuggestion[] {
  return [...list].sort(
    (a, b) =>
      b.matchBreadth - a.matchBreadth ||
      b.maxLevel - a.maxLevel ||
      a.name.localeCompare(b.name)
  );
}

/**
 * Count in-progress requests per SC display name from the opportunities/requests
 * dataset (the same source ODPanel's SC workload table uses). A request counts
 * once per SC it is assigned to; multi-SC names are split. Keyed by display name
 * because that is how `user_sc_name` identifies an SC, matching the suggestion's
 * `name` field.
 */
export function countInProgressBySCName(requests: Request[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const r of requests || []) {
    if (r.req_status !== 'In Progress') continue;
    for (const scName of splitUserSCNames(r.user_sc_name)) {
      counts[scName] = (counts[scName] || 0) + 1;
    }
  }
  return counts;
}

/**
 * Compute SC suggestions for a single request: contributors who are
 * available for the request's distinct mapped solutions. The request's
 * products are deduped to distinct solutions first. Suggestions are ordered by
 * match breadth (available solutions, descending), then by the highest
 * experience level among the matched solutions, then by name. Full matches
 * (available for every distinct mapped solution) always sort before partial
 * matches because their breadth is maximal.
 */
export function computeRequestSuggestions(
  request: Pick<PendingRequest, 'products'>,
  state: TrackerAvailabilityState,
  today: string,
  inProgressBySCName: Record<string, number> = {}
): RequestSuggestions {
  const contributors = state.contributors ?? [];
  const seen = new Set<string>();
  const mapped: { product: string; solutionId: string }[] = [];
  for (const product of request.products ?? []) {
    const solutionId = productToSolutionId(product);
    if (!solutionId || seen.has(solutionId)) continue;
    seen.add(solutionId);
    mapped.push({ product, solutionId });
  }
  if (mapped.length === 0) return { full: [], partial: [] };

  const full: RequestSuggestion[] = [];
  const partial: RequestSuggestion[] = [];

  for (const contributor of contributors) {
    if (isOutOfOfficeToday(contributor, today)) continue;

    const matches: SuggestionProductMatch[] = mapped.map(m => ({
      product: m.product,
      solutionId: m.solutionId,
      level: state.experienceLevels?.[contributor.id]?.[m.solutionId] ?? null,
      status: effectiveAvailability(state, contributor.id, m.solutionId, today),
    }));

    const matched = matches.filter(m => m.status === 'available');
    if (matched.length === 0) continue;

    const suggestion: RequestSuggestion = {
      contributorId: contributor.id,
      name: contributor.name || contributor.id,
      email: contributor.email,
      region: contributor.region,
      matches,
      full: matched.length === matches.length,
      matchBreadth: matched.length,
      maxLevel: Math.max(0, ...matched.map(m => levelRank(m.level))),
      inProgressCount: inProgressBySCName[contributor.name || contributor.id] ?? 0,
    };
    (suggestion.full ? full : partial).push(suggestion);
  }

  return { full: sortSuggestions(full), partial: sortSuggestions(partial) };
}
